import React from 'react'
import { Participant } from '../types'
import { SubtitleCue } from './SubtitleParser'
import { SubtitleTrackOption, formatAddedDate } from './useSubtitles'
import styles from './styles'

interface SubtitleOverlayProps {
  currentCue: SubtitleCue | null
  // Other lines spoken at the same moment (a split line, two people at once):
  // drawn as their own boxes, stacked under the current one.
  simultaneousCues?: SubtitleCue[]
  participants: Participant[]
  // Final rendered text size in px (container base × user preset). Avatar and
  // box chrome scale proportionally off it.
  fontSize?: number
  // Drives the vertical anchor: cues sit above the control bar while it is
  // visible and slide down toward the bottom edge when the UI idle-fades.
  controlsVisible?: boolean
  // The control bar's measured height in px. Cues clear it by a small gap
  // while it is visible; 0 (not yet measured) falls back to the old offset.
  controlsHeight?: number
  // T18 (docs/plans/2026-08-13-verification-integration-closeout.md): which
  // track the currently displayed cue came from — omit for exactly today's
  // rendering (no badge at all). §3.7 of the manifest design requires the
  // viewer be told a sealed track and an addendum are NOT the same claim,
  // so this is the one place that distinction becomes visible per cue.
  activeTrack?: SubtitleTrackOption | null
}

// "Sealed with the video" is never spelled out — it's the absence of a
// badge, which is what "exactly today's rendering" for every base track
// requires. An addendum always gets one, worded by whether it is anchored:
// an anchored addendum's date is the CHAIN's clock (activeTrack.addedAt),
// never a local build time. An unanchored addendum (D18 in the Discovered
// Issues Log) has no chain hash and no chain timestamp, so its copy must not
// imply it is proven the way an anchored one is.
function addendumBadgeText(track: SubtitleTrackOption): string {
  if (track.anchored && track.addedAt) {
    return `Added by the publisher on ${formatAddedDate(track.addedAt)}`
  }
  return 'Added by the publisher — not yet confirmed on chain'
}

// Breathing room between the top of the control bar and the caption box.
const CONTROLS_GAP = 8

const SubtitleOverlay: React.FC<SubtitleOverlayProps> = ({ currentCue, simultaneousCues = [], participants, fontSize = 16, controlsVisible = true, controlsHeight = 0, activeTrack = null }) => {
  if (!currentCue) return null

  const avatarSize = fontSize * 1.7
  const gap = fontSize * 0.5
  // Only an addendum ever gets a badge — a base track (or no activeTrack at
  // all, e.g. every caller before T18) renders exactly as it did before this
  // prop existed.
  const isAddendum = activeTrack?.kind === 'addendum'
  const together = simultaneousCues.length > 0

  return (
    <div
      style={{
        ...styles.subtitleContainer,
        bottom: controlsVisible
          ? `${controlsHeight > 0 ? controlsHeight + CONTROLS_GAP : 56}px`
          : '12px',
        // Stacking the badge, or a second speaker's box, needs a column
        // axis; every other case keeps the original row layout untouched.
        ...(isAddendum || together ? { flexDirection: 'column' as const, alignItems: 'center' as const } : {}),
        ...(together ? { gap: `${fontSize * 0.3}px` } : {}),
      }}
    >
      {isAddendum && activeTrack && (
        <div style={{
          ...styles.subtitleTrackBadge,
          fontSize: `${fontSize * 0.65}px`,
          marginBottom: `${fontSize * 0.3}px`,
        }}>
          {addendumBadgeText(activeTrack)}
        </div>
      )}
      {[currentCue, ...simultaneousCues].map((cue, i) => (
        <CueBox key={i} cue={cue} participants={participants} fontSize={fontSize} avatarSize={avatarSize} gap={gap} />
      ))}
    </div>
  )
}

interface CueBoxProps {
  cue: SubtitleCue
  participants: Participant[]
  fontSize: number
  avatarSize: number
  gap: number
}

const CueBox: React.FC<CueBoxProps> = ({ cue: currentCue, participants, fontSize, avatarSize, gap }) => {
  const participant = participants.find(p => p.id === currentCue.speaker)
  // Speaker colour lives on the avatar chip only — cue text is always white so
  // it stays readable over any video.
  const color = participant?.color || '#fff'
  const speakerName = participant?.name || currentCue.speaker || '?'
  const initial = speakerName[0].toUpperCase()

  return (
      <div style={{
        ...styles.subtitleBox,
        gap: `${gap}px`,
        padding: `${fontSize * 0.5}px ${fontSize}px`,
        borderRadius: `${fontSize * 1.25}px`,
      }}>
        {currentCue.speaker && (
          <div style={{
            ...styles.subtitleAvatar,
            width: `${avatarSize}px`,
            height: `${avatarSize}px`,
            fontSize: `${fontSize * 0.85}px`,
            backgroundColor: participant?.avatar_url ? 'transparent' : color,
            color: '#fff',
          }}>
            {participant?.avatar_url ? (
              <img
                src={participant.avatar_url}
                alt={speakerName}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              initial
            )}
          </div>
        )}
        <span style={{ ...styles.subtitleText, fontSize: `${fontSize}px`, color: '#fff' }}>{currentCue.text}</span>
      </div>
  )
}

export default SubtitleOverlay
